import { useQuery } from '@tanstack/react-query';
import { getProfile } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useClientSelection } from '../contexts/ClientSelectionContext';

type ModuleId = 'shipping' | 'rma' | 'claims' | 'replenishment' | 'procurement' | 'products' | 'orders' | 'warehousing' | 'receiving';

const ALL_MODULES: ModuleId[] = ['shipping', 'rma', 'claims', 'replenishment', 'procurement', 'products', 'orders', 'warehousing', 'receiving'];

// Modules that are always enabled for all users (no client toggle needed)
const ALWAYS_ENABLED: ModuleId[] = ['claims', 'procurement'];

/**
 * Hook to get the list of enabled modules for the current user's client.
 * Admin users always see all modules regardless of client settings.
 * Some modules (e.g. claims) are always enabled for everyone.
 */
export function useEnabledModules(): {
  enabledModules: ModuleId[];
  isLoading: boolean;
  error: Error | null;
} {
  const { user } = useAuth();
  const { selectedStoreModules } = useClientSelection();
  const { data: profile, isLoading, error } = useQuery({
    queryKey: ['profile'],
    queryFn: getProfile,
    staleTime: 5 * 60 * 1000, // Consider fresh for 5 minutes
  });

  // Admins always see all modules — they select a client per module
  if (user?.isAdmin) {
    return { enabledModules: ALL_MODULES, isLoading, error: error as Error | null };
  }

  // Multi-store users: modules follow the SELECTED store (context resolves
  // them from profile.assignedClients); single-store users keep the
  // profile-level modules as before.
  const clientModules =
    (selectedStoreModules as ModuleId[] | undefined) ||
    (profile?.enabledModules as ModuleId[]) ||
    ['shipping', 'products', 'orders', 'warehousing'];

  // Merge client-enabled modules with always-enabled modules
  const enabledModules = [...new Set([...clientModules, ...ALWAYS_ENABLED])];

  return {
    enabledModules,
    isLoading,
    error: error as Error | null,
  };
}
